import gql from 'graphql-tag'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import set from 'lodash/set'
import React, { useContext } from 'react'
import { useParams } from 'react-router'
import Event from '../../api/events/model'
import { useQuery } from '../../api/graphql'
import CommunitiesContextProvider from '../../hooks/CommunitiesContextProvider'
import useEventFormData from '../../hooks/useEventFormData'
import { CurrentUserContext } from '../../providers/CurrentUserContext'
import Routes from '../../startup/routes'
import useRecordLookup from '../../hooks/useRecordLookup'
import { RecordLoadFailed, RecordNotFound } from '../components/misc/RecordNotFound'
import EventEdit from '../components/events/EventEdit'

const EventEditQuery = gql`
  query EventEditQuery($slug: String!) {
    event(slug: $slug) {
      id
      slug
      title
      description
      category
      subcategory
      dressCode
      image_publicId: imagePublicId
      # Carried through the edit round-trip so a save that leaves the image
      # alone re-sends the size that describes it. Replacing the image
      # overwrites all three at once (MediaUploadField).
      image_width: imageWidth
      image_height: imageHeight
      startAt
      endAt
      # Phase 9 (design rev.5 §1 Outbound): the event's start/end in its
      # venue-resolved DISPLAY zone, naive "YYYY-MM-DDTHH:MM" (EventType,
      # Phase 6). The edit form seeds its date/time fields from THESE (via
      # editFormDateTimeFields) instead of browser-re-zoning the ISO instant,
      # making the edit round-trip symmetric. Additive — startAt/endAt above are
      # kept (other consumers still use the absolute instant).
      startAtLocal
      endAtLocal
      costTier
      costMin
      costMax
      eventType
      repeats
      repeatStopsOn
      repeatingEventId
      publishState
      tags
      ageRanges
      useTempPlace
      submitterOwnsEvent
      needTicketOrRsvp
      ticketOrRsvpUrl

      # TODO: many-to-many
      communities {
        id
        displayName
        liveState
        childCommunities {
          nodes {
            id
            liveState
          }
        }
      }
      place {
        id
        slug
        name
      }

      submitter {
        id
      }

      organizer_email: organizerEmail
      organizer_name: organizerName
      organizer_phone: organizerPhone

      submittedOnBehalfOfOrg {
        id
        name
        contactEmail
        phone
      }

      tempPlace_name: tempPlaceName
      tempPlace_url: tempPlaceUrl
      tempPlace_address_city: tempPlaceAddressCity
      tempPlace_address_state: tempPlaceAddressState
      tempPlace_address_streetAddress: tempPlaceAddressStreetAddress
      tempPlace_address_streetAddress2: tempPlaceAddressStreetAddress2
      tempPlace_address_zipCode: tempPlaceAddressZipCode

      isVirtual
      url
      internalOnly
    }
  }
`

const EventEditContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { loading: loadingCurrentUser } = useContext(CurrentUserContext)
  const { data, loading } = useEventFormData()

  const { data: eventData, loading: loadingEvent, error: eventError } = useQuery(EventEditQuery, {
    variables: { slug: id },
    skip: loadingCurrentUser,
  })

  // {...event} is needed to convert Event to JS object w/o functions
  // for example `omitBy(isNil)` assigned place.fullAddress as a value
  const event = new Event(omitBy({ ...eventData?.event }, isNil))
  event.organizer = omitBy(event.organizer, isNil)
  event.tempPlace = omitBy(event.tempPlace, isNil)
  set(event, 'tempPlace.address', omitBy(event.tempPlace?.address, isNil))
  set(event, 'placeId', event.place?.id)
  event.primaryCommunityId = event.community?.id
  // Preserve submittedOnBehalfOfOrg for organizerType derivation
  event.submittedOnBehalfOfOrg = eventData?.event?.submittedOnBehalfOfOrg

  // An edit link whose event is removed, never existed or isn't one you can see (CVL-138).
  const lookup = useRecordLookup({ loading: loadingCurrentUser || loadingEvent, error: eventError, record: eventData?.event })
  if (lookup === 'failed') return <RecordLoadFailed />
  if (lookup === 'notFound') return <RecordNotFound kind="event" backTo={Routes.yourEvents()} backLabel="Back to Your Events" />

  return (
    <CommunitiesContextProvider value={data?.communities}>
      <EventEdit loading={loading || loadingCurrentUser || loadingEvent} event={event} />
    </CommunitiesContextProvider>
  )
}

export default EventEditContainer
